import { useDelay } from '@sparemin/blockhead';
import { useCallback, useState } from 'react';

/**
 * how long playback must stay stalled before the loading indicator appears,
 * which avoids a flash when a brief stall resolves almost immediately (e.g.
 * Safari fires a short `waiting`/`canplay` cycle on a cold start)
 */
const BUFFERING_INDICATOR_DELAY_MS = 250;

export type BufferingIndicator = {
  isBuffering: boolean;
  /**
   * hides the indicator and cancels any pending show; call when playback
   * resumes or stops
   */
  hideBuffering: () => void;
  /**
   * shows the indicator unless playback resumes within the delay; call when
   * playback stalls for data
   */
  showBuffering: () => void;
};

/**
 * Tracks whether a media element should show a loading indicator, debouncing
 * the show so a stall that resolves almost immediately doesn't flash it.
 */
export default function useBufferingIndicator(): BufferingIndicator {
  const [isBuffering, setIsBuffering] = useState(false);

  const [showBuffering, cancelShow] = useDelay(() => {
    setIsBuffering(true);
  }, BUFFERING_INDICATOR_DELAY_MS);

  const hideBuffering = useCallback(() => {
    cancelShow();
    setIsBuffering(false);
  }, [cancelShow]);

  return {
    hideBuffering,
    isBuffering,
    showBuffering,
  };
}
